import { useMediaQuery } from '@chakra-ui/react';
import { useAuth } from '@devd-client/devd/auth';
import {
  durationData,
  PeriodType,
  removeEmptyQueryParams,
  useDashboardStore,
  useQueryState,
  RepoDto,
} from '@devd-client/devd/components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useFetchRepo } from '../api';
import { useGitCards, QUERY_KEYS } from '../graphql';

export const useGitDashboard = () => {
  const [isLargerThan1820] = useMediaQuery('(min-width: 1820px)');
  const [isLargeThan2120] = useMediaQuery('(min-width: 2120px)');
  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [team, setTeam] = useQueryState('team');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [metricType, setMetricType] = useQueryState('tab');
  const [filter, setFilter] = useQueryState('filter');
  const [repo, setRepo] = useQueryState('repo');
  const [appState, dispatch] = useAuth();
  const [periodOptions, setPeriodOptions] =
    useState<PeriodType[]>(durationData);
  const { data: repoList, isLoading: repoLoading } = useFetchRepo('git');

  const { selectedTeam, selected, setSelectedTeam, setSelected } =
    useDashboardStore();

  const [type, setType] = useState<string>();
  const [repos, setRepos] = useState<{ value: string; label: string }[]>([]);

  const [filterBy, setFilterBy] = useState<{
    value: string;
    label: string;
  }>({ value: 'team', label: 'Team' });

  const { data, isFetching } = useGitCards(
    selected.startDate,
    selected.endDate,
    selected?.sprintId,
    filterBy.value === 'repo' ? '' : selectedTeam.value,
    filterBy.value === 'team'
      ? []
      : repos?.map((el: { value: string; label: string }) => el.value)
  );

  const [clickedTab, setClickedTab] = useState<string>();

  useEffect(() => {
    if (!data) return;
    if (!clickedTab && !type) {
      setClickedTab(data[0]?.chartKey);
      setType(data[0]?.chartKey);
    }
  }, [data]);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }

    if (typeof selectedTeam !== 'undefined') {
      selectedTeam.label === 'Organisation'
        ? setTeam('Organisation')
        : setTeam(selectedTeam.value);
    }

    if (type) {
      setMetricType(type);
    }

    if (repos && repos?.length < 1) {
      removeEmptyQueryParams({ repo: '' });
    } else {
      setRepo(
        repos
          ?.map((repo: { value: string; label: string }) => repo.label)
          .join(', ')
      );
    }

    if (filterBy.value) {
      setFilter(filterBy.value);
    }
  }, [selected, selectedTeam.value, type, repos, filterBy.value]);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    filter &&
      setFilterBy(
        filter === 'team'
          ? { value: 'team', label: 'Team' }
          : filter === 'repo'
          ? { value: 'repo', label: 'Repo' }
          : { value: '', label: '' }
      );

    metricType && setType(metricType);
    metricType && setClickedTab(metricType);

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  useEffect(() => {
    if (!repoList?.dto) return;
    repo &&
      setRepos(
        repoList?.dto
          ?.filter((el: RepoDto) => repo?.split(', ')?.includes(el.name))
          ?.map((item: RepoDto) => ({ value: item.key, label: item.name }))
      );
  }, [repoList?.dto, repo]);

  return {
    isLargerThan1820,
    isLargeThan2120,
    selected,
    selectedTeam,
    setSelectedTeam,
    filterBy,
    setFilterBy,
    repos,
    setRepos,
    appState,
    dispatch,
    clickedTab,
    setClickedTab,
    type,
    setType,
    repoList,
    repoLoading,
    QUERY_KEYS,
    data,
    isFetching,
  };
};
